/**
 * iter1037 — Data Lineage Popover (feature #17).
 *
 * A small info chip that, on click, reveals where a metric value comes from.
 * Sources can be:
 *   - Lab document (with date + name)
 *   - Manual profile entry (with date)
 *   - AI extraction (OCR, with confidence)
 *   - Meal log aggregate (with N days)
 *
 * Usage:
 *   <DataLineagePopover sources={[
 *     { kind: "lab", label: "DUTCH test", date: "2026-06-12", confidence: 0.9 },
 *     { kind: "manual", label: "Ръчно въвеждане", date: "2026-07-05" },
 *   ]}>
 *     <span>27.3</span>
 *   </DataLineagePopover>
 */
import React, { useState, useRef, useEffect } from "react";
import { Info, FileText, Edit3, Sparkles, Utensils, X, Upload, AlertCircle } from "lucide-react";
import { Link } from "react-router-dom";

const KIND_META = {
  lab:      { icon: FileText, label_bg: "Лабораторен документ",   tint: "text-pink-500 bg-pink-500/10" },
  manual:   { icon: Edit3,    label_bg: "Ръчно въвеждане",        tint: "text-emerald-500 bg-emerald-500/10" },
  ai:       { icon: Sparkles, label_bg: "AI извличане (OCR)",     tint: "text-purple-500 bg-purple-500/10" },
  meal_log: { icon: Utensils, label_bg: "Хранителен дневник",     tint: "text-orange-500 bg-orange-500/10" },
  computed: { icon: Info,     label_bg: "Изчислено",              tint: "text-sky-500 bg-sky-500/10" },
  missing:  { icon: AlertCircle, label_bg: "Липсваща лаборатория", tint: "text-amber-500 bg-amber-500/10" },
};

// iter1038 — Patient-friendly glossary. Shown as a small footnote in the popover.
// Metric key → plain-BG explanation.
const METRIC_GLOSSARY = {
  "hs-crp":        "hs-CRP · показва възпаление в тялото. Ниските нива означават спокойни клетки.",
  "hs_crp":        "hs-CRP · показва възпаление в тялото. Ниските нива означават спокойни клетки.",
  "homa-ir":       "HOMA-IR · измерва как клетките реагират на инсулин. По-нисък = по-добра инсулинова чувствителност.",
  "homa_ir":       "HOMA-IR · измерва как клетките реагират на инсулин. По-нисък = по-добра инсулинова чувствителност.",
  "insulin":       "Инсулин на гладно · хормонът, който регулира кръвната захар.",
  "estradiol":     "Естрадиол (E2) · основният женски полов хормон. Влияе върху гърдения метаболизъм.",
  "hba1c":         "HbA1c · средна кръвна захар за последните 3 месеца.",
  "igf-1":         "IGF-1 · растежен фактор, свързан с инсулина. Влияе на клетъчния растеж.",
  "igf_1":         "IGF-1 · растежен фактор, свързан с инсулина. Влияе на клетъчния растеж.",
  "shbg":          "SHBG · протеин, който свързва половите хормони. По-висок обикновено = по-добра хормонална баланс.",
  "vitamin_d":     "Витамин D · регулира имунната система и калциевия метаболизъм.",
  "ferritin":      "Феритин · показва запасите от желязо в тялото.",
  "tsh":           "TSH · щитовиден стимулиращ хормон. Регулира щитовидната жлеза.",
  "bmi":           "BMI · съотношение тегло/ръст².",
  "waist":         "Талия · маркер за висцерална мазнина.",
  "waist_cm":      "Талия · маркер за висцерална мазнина.",
};

export default function DataLineagePopover({ sources = [], children, testid, glossaryKeys = [] }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  if (!sources.length && !glossaryKeys.length) return children;

  // iter1038 — detect missing lab source to surface CTA
  const missingLabs = sources.filter((s) => s.kind === "missing");
  const glossary = (glossaryKeys || [])
    .map((k) => METRIC_GLOSSARY[(k || "").toLowerCase()])
    .filter(Boolean);

  return (
    <span className="inline-flex items-center gap-1 relative" ref={ref}>
      {children}
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen((v) => !v); }}
        aria-label="Виж източниците"
        data-testid={testid || "data-lineage-trigger"}
        className="inline-flex items-center justify-center w-4 h-4 rounded-full text-slate-400 hover:text-sky-500 hover:bg-sky-500/10 transition"
      >
        <Info className="w-3 h-3" />
      </button>

      {open && (
        <>
          {/* iter1038 — mobile backdrop to help user tap outside on small screens */}
          <div
            className="fixed inset-0 z-40 sm:hidden"
            onClick={(e) => { e.stopPropagation(); setOpen(false); }}
            aria-hidden
          />
          <div
            className="fixed sm:absolute z-50 left-3 right-3 sm:left-1/2 sm:right-auto top-auto bottom-3 sm:top-full sm:bottom-auto sm:mt-1 sm:-translate-x-1/2 sm:w-72 max-w-[calc(100vw-1.5rem)] rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-xl overflow-hidden text-left"
            data-testid="data-lineage-popover"
          >
          <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100 dark:border-slate-800">
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Източници на данни</div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="w-6 h-6 rounded-full flex items-center justify-center text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
          <ul className="divide-y divide-slate-100 dark:divide-slate-800 max-h-[50vh] overflow-y-auto">
            {sources.map((s, i) => {
              const meta = KIND_META[s.kind] || KIND_META.computed;
              const Icon = meta.icon;
              return (
                <li key={i} className="px-3 py-2 flex items-start gap-2">
                  <div className={`w-6 h-6 rounded-md flex items-center justify-center shrink-0 ${meta.tint}`}>
                    <Icon className="w-3.5 h-3.5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold break-words text-slate-900 dark:text-slate-100">
                      {s.label || meta.label_bg}
                    </div>
                    <div className="text-[10px] text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
                      {s.date && <span>{s.date}</span>}
                      {typeof s.confidence === "number" && (
                        <span className="px-1.5 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800">
                          {(s.confidence * 100).toFixed(0)}% сигурност
                        </span>
                      )}
                      {typeof s.weight === "number" && (
                        <span className="px-1.5 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800">
                          принос {(s.weight * 100).toFixed(0)}%
                        </span>
                      )}
                    </div>
                    {s.detail && (
                      <div className="text-[10px] text-slate-500 mt-0.5 break-words">{s.detail}</div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>

          {/* iter1038 — Missing labs CTA: bridges BHI ↔ Labs gap */}
          {missingLabs.length > 0 && (
            <Link
              to="/dashboard/analyses"
              onClick={() => setOpen(false)}
              data-testid="data-lineage-upload-labs-cta"
              className="flex items-start gap-2 px-3 py-2.5 bg-amber-50 dark:bg-amber-900/20 border-t border-amber-200 dark:border-amber-900/40 hover:bg-amber-100 dark:hover:bg-amber-900/30 transition"
            >
              <Upload className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="text-xs font-bold text-amber-800 dark:text-amber-200">
                  Качи липсващите лаборатории
                </div>
                <div className="text-[10px] text-amber-700 dark:text-amber-300 mt-0.5 break-words">
                  Точността на BHI може да нарастне с ~40% след добавяне на: {missingLabs.map((m) => m.label).join(", ")}
                </div>
              </div>
            </Link>
          )}

          {/* iter1038 — Plain-language glossary footnote */}
          {glossary.length > 0 && (
            <div className="px-3 py-2 border-t border-slate-100 dark:border-slate-800 bg-sky-50/60 dark:bg-sky-900/10">
              <div className="text-[10px] font-bold uppercase tracking-wider text-sky-600 dark:text-sky-400 mb-1">
                Какво означава?
              </div>
              <ul className="space-y-1">
                {glossary.map((g, i) => (
                  <li key={i} className="text-[10px] text-slate-600 dark:text-slate-300 leading-snug">
                    {g}
                  </li>
                ))}
              </ul>
            </div>
          )}
          </div>
        </>
      )}
    </span>
  );
}
